import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    Grid,
    Paper,
    Divider,
    Chip,
    Alert,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    SportsEsports as GameIcon,
    EmojiEvents as TrophyIcon,
    Timer as TimerIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';

const GamePointsConfigDialog = ({ open, onClose, game, onSave }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [gameType, setGameType] = useState('challenge');
    const [pointsConfig, setPointsConfig] = useState({});
    const [matchLimits, setMatchLimits] = useState({
        max_matches_per_opponent: 3,
        max_total_matches: 3
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (game) {
            // Initialize with game's current configuration
            setGameType(game.game_type || 'challenge');
            setPointsConfig(game.points_config || {});
            setMatchLimits(game.match_limits || {
                max_matches_per_opponent: 3,
                max_total_matches: 3
            });
        }
    }, [game]);

    const handlePointChange = (field, value) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue) || numValue < 0 || numValue > 200) {
            setErrors({ ...errors, [field]: 'Value must be between 0 and 200' });
        } else {
            setErrors({ ...errors, [field]: null });
        }
        setPointsConfig({ ...pointsConfig, [field]: numValue });
    };

    const handleMatchLimitChange = (field, value) => {
        const numValue = value === '' || value === null ? null : parseInt(value, 10);
        setMatchLimits({ ...matchLimits, [field]: numValue });
    };

    const handleGameTypeChange = (newGameType) => {
        setGameType(newGameType);
        
        // Initialize points_config with default values for the new game type
        if (newGameType === 'session') {
            setPointsConfig({
                first_place: 45,
                second_place: 35,
                third_place: 24,
                fourth_place: 15,
                fifth_place: 10,
                sixth_place: 8,
                seventh_place: 6,
                eighth_place: 4,
                ninth_to_sixteenth_place: 2
            });
        } else if (newGameType === 'challenge') {
            setPointsConfig({
                first_win: 10,
                second_win: 8,
                third_win: 6,
                first_loss: 2,
                second_loss: 4,
                third_loss: 6
            });
        } else if (newGameType === 'time_based') {
            setPointsConfig({
                first_place: 60,
                second_place: 45,
                third_place: 35,
                fourth_place: 24,
                fifth_place: 15,
                sixth_place: 10,
                seventh_place: 8,
                eighth_place: 6,
                ninth_to_sixteenth_place: 4
            });
        }
    };

    const handleSave = () => {
        // Validate all fields
        const hasErrors = Object.values(errors).some(error => error !== null);
        if (hasErrors) {
            return;
        }

        onSave({
            game_type: gameType,
            points_config: pointsConfig,
            match_limits: matchLimits
        });
        onClose();
    };

    const calculateTotal = () => {
        if (gameType === 'challenge') {
            return (pointsConfig.first_win || 0) + 
                   (pointsConfig.second_win || 0) + 
                   (pointsConfig.third_win || 0);
        }
        return 0;
    };

    const renderSessionPointsConfig = () => (
        <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
                Configure points awarded based on final placement in sessions. 
                Players receive points for where they finish in the game.
            </Alert>

            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrophyIcon color="primary" />
                Placement Points
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
                {[
                    { key: 'first_place', label: '1st Place', default: 45, color: 'gold' },
                    { key: 'second_place', label: '2nd Place', default: 35, color: 'silver' },
                    { key: 'third_place', label: '3rd Place', default: 24, color: '#CD7F32' },
                    { key: 'fourth_place', label: '4th Place', default: 15 },
                    { key: 'fifth_place', label: '5th Place', default: 10 },
                    { key: 'sixth_place', label: '6th Place', default: 8 },
                    { key: 'seventh_place', label: '7th Place', default: 6 },
                    { key: 'eighth_place', label: '8th Place', default: 4 },
                    { key: 'ninth_to_sixteenth_place', label: '9th - 16th Place', default: 2 },
                ].map((field) => (
                    <Grid item xs={12} sm={6} md={4} key={field.key}>
                        <Paper sx={{ p: 2, ...(field.color && { borderLeft: `4px solid ${field.color}` }) }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {field.label}
                            </Typography>
                            <TextField
                                fullWidth
                                type="number"
                                value={pointsConfig[field.key] ?? field.default}
                                onChange={(e) => handlePointChange(field.key, e.target.value)}
                                error={!!errors[field.key]}
                                helperText={errors[field.key]}
                                inputProps={{ min: 0, max: 200 }}
                                size="small"
                            />
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    const renderChallengePointsConfig = () => (
        <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
                Configure points for wins and losses. Max 3 matches per opponent.
                Points decrease with each subsequent match against the same opponent.
            </Alert>

            <Grid container spacing={3}>
                {/* Wins */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderLeft: '4px solid #4caf50' }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrophyIcon color="success" />
                            Wins
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="1st Win"
                                type="number"
                                value={pointsConfig.first_win ?? 10}
                                onChange={(e) => handlePointChange('first_win', e.target.value)}
                                error={!!errors.first_win}
                                helperText={errors.first_win}
                                inputProps={{ min: 0, max: 200 }}
                                fullWidth
                            />
                            <TextField
                                label="2nd Win"
                                type="number"
                                value={pointsConfig.second_win ?? 8}
                                onChange={(e) => handlePointChange('second_win', e.target.value)}
                                error={!!errors.second_win}
                                helperText={errors.second_win}
                                inputProps={{ min: 0, max: 200 }}
                                fullWidth
                            />
                            <TextField
                                label="3rd Win"
                                type="number"
                                value={pointsConfig.third_win ?? 6}
                                onChange={(e) => handlePointChange('third_win', e.target.value)}
                                error={!!errors.third_win}
                                helperText={errors.third_win}
                                inputProps={{ min: 0, max: 200 }}
                                fullWidth
                            />
                            <Box sx={{ mt: 1, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Total: {calculateTotal()} points
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Losses */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderLeft: '4px solid #f44336' }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GameIcon color="error" />
                            Losses (Participation Points)
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="1st Loss"
                                type="number"
                                value={pointsConfig.first_loss ?? 2}
                                onChange={(e) => handlePointChange('first_loss', e.target.value)}
                                error={!!errors.first_loss}
                                helperText={errors.first_loss}
                                inputProps={{ min: 0, max: 200 }}
                                fullWidth
                            />
                            <TextField
                                label="2nd Loss"
                                type="number"
                                value={pointsConfig.second_loss ?? 4}
                                onChange={(e) => handlePointChange('second_loss', e.target.value)}
                                error={!!errors.second_loss}
                                helperText={errors.second_loss}
                                inputProps={{ min: 0, max: 200 }}
                                fullWidth
                            />
                            <TextField
                                label="3rd Loss"
                                type="number"
                                value={pointsConfig.third_loss ?? 6}
                                onChange={(e) => handlePointChange('third_loss', e.target.value)}
                                error={!!errors.third_loss}
                                helperText={errors.third_loss}
                                inputProps={{ min: 0, max: 200 }}
                                fullWidth
                            />
                            <Box sx={{ mt: 1, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Total: {(pointsConfig.first_loss || 0) + (pointsConfig.second_loss || 0) + (pointsConfig.third_loss || 0)} points
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );

    const renderTimeBasedPointsConfig = () => (
        <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
                Configure points for time trial competitions. Points awarded based on fastest times.
                Higher points rewards for time-based games.
            </Alert>

            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimerIcon color="primary" />
                Time Trial Points
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
                {[
                    { key: 'first_place', label: '1st Place (Fastest)', default: 60, color: 'gold' },
                    { key: 'second_place', label: '2nd Place', default: 45, color: 'silver' },
                    { key: 'third_place', label: '3rd Place', default: 35, color: '#CD7F32' },
                    { key: 'fourth_place', label: '4th Place', default: 24 },
                    { key: 'fifth_place', label: '5th Place', default: 15 },
                    { key: 'sixth_place', label: '6th Place', default: 10 },
                    { key: 'seventh_place', label: '7th Place', default: 8 },
                    { key: 'eighth_place', label: '8th Place', default: 6 },
                    { key: 'ninth_to_sixteenth_place', label: '9th - 16th Place', default: 4 },
                ].map((field) => (
                    <Grid item xs={12} sm={6} md={4} key={field.key}>
                        <Paper sx={{ p: 2, ...(field.color && { borderLeft: `4px solid ${field.color}` }) }}>
                            <Typography variant="subtitle2" gutterBottom>
                                {field.label}
                            </Typography>
                            <TextField
                                fullWidth
                                type="number"
                                value={pointsConfig[field.key] ?? field.default}
                                onChange={(e) => handlePointChange(field.key, e.target.value)}
                                error={!!errors[field.key]}
                                helperText={errors[field.key]}
                                inputProps={{ min: 0, max: 200 }}
                                size="small"
                            />
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    const renderMatchLimits = () => (
        <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
                Configure maximum matches allowed. This prevents point farming and ensures fair competition.
            </Alert>

            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon color="primary" />
                Match Limits
            </Typography>

            <Box sx={{ mt: 2 }}>
                <Paper sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Max Matches per Opponent"
                                type="number"
                                value={matchLimits.max_matches_per_opponent ?? 5}
                                onChange={(e) => handleMatchLimitChange('max_matches_per_opponent', e.target.value)}
                                inputProps={{ min: 1, max: 20 }}
                                fullWidth
                                helperText="Maximum matches allowed against the same opponent"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Max Total Matches (Optional)"
                                type="number"
                                value={matchLimits.max_total_matches ?? ''}
                                onChange={(e) => handleMatchLimitChange('max_total_matches', e.target.value)}
                                inputProps={{ min: 1, max: 100 }}
                                fullWidth
                                helperText="Total matches allowed overall (leave empty for unlimited)"
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
        </Box>
    );

    if (!game) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <GameIcon color="primary" />
                    <Box>
                        <Typography variant="h6">Configure Points: {game.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Chip 
                                label={game.category} 
                                size="small" 
                                color={game.category === 'competitive' ? 'error' : game.category === 'irl' ? 'warning' : 'success'}
                            />
                            <Chip 
                                label={game.game_type?.replace('_', ' ') || 'Challenge'} 
                                size="small" 
                                variant="outlined"
                            />
                        </Box>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label="Game Type & Points" />
                    <Tab label="Match Limits" />
                </Tabs>

                {activeTab === 0 && (
                    <Box>
                        <Box sx={{ mt: 3, mb: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel>Game Type</InputLabel>
                                <Select
                                    value={gameType}
                                    onChange={(e) => handleGameTypeChange(e.target.value)}
                                    label="Game Type"
                                >
                                    <MenuItem value="session">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TrophyIcon fontSize="small" />
                                            Session - Placement-based (Flatout, Golf With Friends, GeoGuessr, WC3)
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="challenge">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <GameIcon fontSize="small" />
                                            Challenge - 1v1 Head-to-head (CS2, Chess, Blobby, Beerpong, Putting)
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="time_based">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TimerIcon fontSize="small" />
                                            Time Based - Time Trials (Asseto Corsa, Trackmania)
                                        </Box>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {gameType === 'session' && renderSessionPointsConfig()}
                        {gameType === 'challenge' && renderChallengePointsConfig()}
                        {gameType === 'time_based' && renderTimeBasedPointsConfig()}
                    </Box>
                )}

                {activeTab === 1 && renderMatchLimits()}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                    Save Configuration
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GamePointsConfigDialog;

